import type { TransactionItem } from '@/types/core-api';

/** Charges and corrections that make up the balance — everything except payments. */
export const CHARGE_TYPES = new Set(['principal', 'interest', 'fee', 'adjustment', 'refund', 'bank_refund', 'closure']);
/** Corrections applied after the debt was raised, as opposed to the original billed components. */
export const ADJUSTMENT_TYPES = new Set(['adjustment', 'refund', 'bank_refund', 'closure']);

/** One charge on the balance — a single ledger transaction. */
export interface ChargeRow {
  rowId: string;
  date: string | null;
  description: string;
  type: string;
  /** Signed euro amount: positive increases the balance, negative decreases it. */
  amount: number;
}

/** `YYYY-MM-DD` with no time part — a calendar date rather than an instant. */
const DATE_ONLY_RE = /^\d{4}-\d{2}-\d{2}$/;

/**
 * Parse a ledger date for display.
 *
 * A date-only value (`issued_on`/`due_on` are DATE columns) is a calendar date, but
 * `new Date('2024-06-01')` reads it as UTC midnight — which formats as the previous day for
 * anyone west of UTC. Build those from local components so the day shown is the day billed.
 * Timestamps keep their instant semantics; Intrum's `YYYY-MM-DD HH:MM:SS` form needs the `T`.
 */
export function parseChargeDate(value: string | null | undefined): Date | null {
  if (!value) return null;

  if (DATE_ONLY_RE.test(value)) {
    const [year, month, day] = value.split('-').map(Number);
    return new Date(year, month - 1, day);
  }

  const parsed = new Date(value.includes('T') ? value : value.replace(' ', 'T'));
  return Number.isNaN(parsed.getTime()) ? null : parsed;
}

/** A ledger date as the customer reads it, or an em-dash when the ledger carries none. */
export function formatLedgerDate(value: string | null | undefined, locale: string): string {
  return parseChargeDate(value)?.toLocaleDateString(locale, { year: 'numeric', month: 'long', day: 'numeric' }) ?? '—';
}

function parseTs(value: string | null | undefined): number {
  return parseChargeDate(value)?.getTime() ?? 0;
}

function description(tx: TransactionItem): string {
  const metaDesc = tx.metadata?.description;
  if (typeof metaDesc === 'string' && metaDesc) return metaDesc;
  return tx.description ?? tx.type ?? '';
}

/** Signed contribution to the balance: charges add, credits and decrease-adjustments subtract. */
function signedAmount(tx: TransactionItem): number {
  const euros = tx.amount_millicents / 100_000;
  if (tx.type === 'adjustment') return tx.metadata?.adjustment_direction === 'decrease' ? -euros : euros;
  return tx.direction === 'credit' ? -euros : euros;
}

function toChargeRow(tx: TransactionItem): ChargeRow {
  return {
    rowId: tx.transaction_id,
    date: tx.created_at,
    description: description(tx),
    type: tx.type ?? '',
    amount: signedAmount(tx),
  };
}

/**
 * Every charge that makes up the balance, newest first — one row per ledger transaction.
 *
 * Flat and exhaustive: this is the primitive that guarantees no charge is ever dropped.
 * {@link listChargesGrouped} layers invoice grouping on top for display; keep that guarantee
 * here so it stays testable on its own.
 */
export function listCharges(transactions: TransactionItem[]): ChargeRow[] {
  return transactions
    .filter((tx) => CHARGE_TYPES.has(tx.type ?? ''))
    .map(toChargeRow)
    .sort((a, b) => parseTs(b.date) - parseTs(a.date));
}

/** The components a client can bill on one invoice. Adjustments correct an invoice, so never join one. */
const INVOICE_COMPONENT_TYPES = new Set(['principal', 'interest', 'fee']);

/** Display order within an invoice — the order a bill reads, not the order the ledger stored them. */
const COMPONENT_ORDER = ['principal', 'interest', 'fee'];

/** Charges billed together on one invoice, as evidenced by their shared reference stem. */
export interface InvoiceGroup {
  kind: 'invoice';
  /** The shared reference stem. Opaque plumbing — never rendered to a customer. */
  groupId: string;
  amount: number;
  /** Always two or more, ordered principal → interest → fee. */
  charges: ChargeRow[];
}

/** A charge with no invoice siblings — the only kind most clients produce. */
export interface StandaloneCharge {
  kind: 'charge';
  charge: ChargeRow;
}

export type ChargeListItem = InvoiceGroup | StandaloneCharge;

/**
 * The invoice a charge belongs to, or `null` when nothing links it to one.
 *
 * Connect derives the key from the client's ledger reference and sends only a digest — the
 * reference itself embeds client account and contract numbers and never reaches the browser.
 * A charge whose reference carried no invoice structure arrives with no key and stands alone,
 * so a client whose format we don't recognise degrades to a flat list, never a wrong grouping.
 */
export function invoiceKey(tx: TransactionItem): string | null {
  return tx.type && INVOICE_COMPONENT_TYPES.has(tx.type) ? (tx.invoice_group_key ?? null) : null;
}

/**
 * Charges as the portal displays them: invoices grouped, everything else standalone.
 *
 * Every row from {@link listCharges} appears exactly once — grouping changes how charges are
 * arranged, never whether they are shown.
 */
export function listChargesGrouped(transactions: TransactionItem[]): ChargeListItem[] {
  const keyByRowId = new Map(transactions.map((tx) => [tx.transaction_id, invoiceKey(tx)]));
  const rows = listCharges(transactions);
  const buckets = new Map<string, ChargeRow[]>();

  for (const row of rows) {
    const key = keyByRowId.get(row.rowId);
    if (key) buckets.set(key, [...(buckets.get(key) ?? []), row]);
  }

  const items: ChargeListItem[] = [];
  for (const row of rows) {
    const key = keyByRowId.get(row.rowId);
    const bucket = key ? buckets.get(key) : undefined;

    // A repeated type in one bucket means the stem spans two invoices rather than identifying one.
    // No client does this today; if one ever does, fall back to separate rows rather than merge.
    const isInvoice = bucket && bucket.length >= 2 && new Set(bucket.map((c) => c.type)).size === bucket.length;

    if (!isInvoice) {
      items.push({ kind: 'charge', charge: row });
      continue;
    }
    // Emit the group once, at the position of its first-encountered component.
    if (bucket[0].rowId !== row.rowId) continue;

    items.push({
      kind: 'invoice',
      groupId: key as string,
      amount: bucket.reduce((sum, c) => sum + c.amount, 0),
      charges: [...bucket].sort((a, b) => COMPONENT_ORDER.indexOf(a.type) - COMPONENT_ORDER.indexOf(b.type)),
    });
  }

  // Every invoice on a case shares one date — the placement load, not a billing date — so date
  // alone leaves the order to sort stability. The ordinal label names a position, so make it total.
  return items.sort((a, b) => {
    const [rowA, rowB] = [itemDate(a), itemDate(b)];
    if (rowA !== rowB) return rowB - rowA;
    const [amountA, amountB] = [itemAmount(a), itemAmount(b)];
    if (amountA !== amountB) return amountB - amountA;
    return itemId(a).localeCompare(itemId(b));
  });
}

function itemDate(item: ChargeListItem): number {
  return parseTs(item.kind === 'invoice' ? item.charges[0].date : item.charge.date);
}

function itemAmount(item: ChargeListItem): number {
  return item.kind === 'invoice' ? item.amount : item.charge.amount;
}

function itemId(item: ChargeListItem): string {
  return item.kind === 'invoice' ? item.groupId : item.charge.rowId;
}

/**
 * What the charges are for, when they all agree — otherwise `null`.
 *
 * Grouped invoices carry no per-row description: every invoice on a case repeats the same one
 * (verified across the book — no case has two), so it names the case's subject rather than any
 * one invoice. Stated once above the list it is the only thing telling the customer what they
 * were billed for; repeated per row it is noise. Disagreement means it isn't a case-level fact,
 * so nothing is claimed.
 */
export function chargesSubject(transactions: TransactionItem[]): string | null {
  const descriptions = new Set(listCharges(transactions).map((row) => row.description.trim()).filter(Boolean));
  return descriptions.size === 1 ? [...descriptions][0] : null;
}

/**
 * Net movement from adjustments, per component, derived from the transactions themselves.
 *
 * The ledger's `adjustment_*_millicents` fields sum only the credit side of the adjustment
 * transfer codes, so an increase posted as a debit is dropped and the total reads as the
 * decreases alone. Netting the signed transactions here keeps the Adjustments rows consistent
 * with the movements listed beneath them.
 */
export function adjustmentTotals(transactions: TransactionItem[]): { principal: number; interest: number; fees: number; total: number } {
  const totals = { principal: 0, interest: 0, fees: 0, total: 0 };

  for (const tx of transactions) {
    if (!ADJUSTMENT_TYPES.has(tx.type ?? '')) continue;

    const amount = signedAmount(tx);
    // Ledger default when a client's adjustment omits the type — see forest.ledger.tasks.other.
    const component = tx.metadata?.adjustment_type ?? 'interest';
    if (component === 'principal' || component === 'interest' || component === 'fees') totals[component] += amount;
    totals.total += amount;
  }

  return totals;
}
